import { API_BASE_URL } from "@kupikupi/brand";
import { useInfiniteQuery, useMutation, useQuery } from "@tanstack/react-query";
import { type CaptchaTokenProvider, withCaptchaRetry } from "../captcha-retry";
import type { ChannelAlias } from "../channel-map";
import { getProductImage, searchProductsByImage, uploadProductImage } from "../endpoints/product";
import { useBackendLang } from "../lang-context";
import type { ProductImageSearchInput } from "../path-adapter";

interface CaptchaProviderOpts {
  /** Provider that resolves a Yandex SmartCaptcha token when the backend
   * returns ApiError("4601"). All image endpoints are rate-limited by
   * SearchAbuseGuard. `| undefined` so consumers can forward
   * `useCaptchaTokenProvider()` directly under exactOptionalPropertyTypes. */
  captchaTokenProvider?: CaptchaTokenProvider | undefined;
}

/**
 * Two-step image search flow:
 *  1. `useUploadProductImage()` mutation — POST base64, get back `imageId`.
 *  2. `useProductImageSearchPaged({ imageId, channel, ... })` — paged
 *     similarity results. No sort/filter; backend ignores those params.
 *
 * Caller chooses the channel (default: 1688/Taobao). Same channel must be
 * used in both steps — backend pins results per channel adapter.
 */
export function useUploadProductImage(opts: CaptchaProviderOpts = {}) {
  return useMutation({
    mutationFn: (input: { imageBase64: string; channel: ChannelAlias }) =>
      withCaptchaRetry(
        (captchaToken) =>
          uploadProductImage(
            { baseUrl: API_BASE_URL },
            input,
            captchaToken === undefined ? {} : { captchaToken },
          ),
        opts.captchaTokenProvider,
      ),
  });
}

/**
 * Reactive per-channel upload: turns the stashed `imageBase64` into a
 * channel-native `imageId` for the *currently selected* channel, cached by
 * `[channel, sessionToken]`. This is what makes the catalog channel tabs
 * independent — uploading for 1688 and uploading for Taobao are separate
 * cache entries, so one channel's third-party failure (e.g. Taobao's upload
 * adapter 500ing) doesn't poison the others. `sessionToken` is an opaque
 * per-search id (the base64 itself is too large to put in the query key).
 *
 * `retry: false` — a failed third-party upload is a channel-level outage, not
 * a transient blip; surface it immediately so the UI can say "this channel is
 * unavailable, try another" instead of hammering a dead upstream.
 */
export function useUploadedImageId(
  input: { channel: ChannelAlias | undefined; imageBase64: string | null; sessionToken: string },
  opts: CaptchaProviderOpts = {},
) {
  const { channel, imageBase64, sessionToken } = input;
  return useQuery({
    queryKey: ["product", "image-upload", channel, sessionToken],
    queryFn: () =>
      withCaptchaRetry(
        (captchaToken) =>
          uploadProductImage(
            { baseUrl: API_BASE_URL },
            { imageBase64: imageBase64 as string, channel: channel as ChannelAlias },
            captchaToken === undefined ? {} : { captchaToken },
          ),
        opts.captchaTokenProvider,
      ),
    enabled: !!channel && !!imageBase64 && !!sessionToken,
    staleTime: Number.POSITIVE_INFINITY,
    retry: false,
  });
}

/**
 * Fetches the uploaded image's base64 back from `/product/image?id=X` so the
 * catalog banner can show the user a thumbnail of what they searched. Returns
 * the raw base64 string (no data-URL prefix) — the consumer prepends one.
 * Cached longer than search results: the bytes never change for a given id.
 */
export function useProductImage(imageId: string) {
  return useQuery({
    queryKey: ["product", "image", imageId],
    queryFn: () => getProductImage({ baseUrl: API_BASE_URL }, imageId),
    staleTime: 5 * 60 * 1000,
    enabled: !!imageId,
  });
}

/**
 * URL variant: extension/plugin scenario where catalog receives ?imageSearchUrl=
 * and uses that URL to call upload to get a channel-native imageId.
 * Backend downloads the image server-side against a domain allowlist (no SSRF
 * risk on the frontend). Cached per [channel, imageUrl] — same logic as the
 * base64 session variant, but keyed on the URL instead of sessionToken.
 */
export function useUploadedImageIdFromUrl(
  input: { channel: ChannelAlias | undefined; imageUrl: string | null },
  opts: CaptchaProviderOpts = {},
) {
  const { channel, imageUrl } = input;
  return useQuery({
    queryKey: ["product", "image-upload-url", channel, imageUrl],
    queryFn: () =>
      withCaptchaRetry(
        (captchaToken) =>
          uploadProductImage(
            { baseUrl: API_BASE_URL },
            { imageUrl: imageUrl as string, channel: channel as ChannelAlias },
            captchaToken === undefined ? {} : { captchaToken },
          ),
        opts.captchaTokenProvider,
      ),
    enabled: !!channel && !!imageUrl,
    staleTime: Number.POSITIVE_INFINITY,
    retry: false,
  });
}

export function useProductImageSearchPaged(
  input: ProductImageSearchInput,
  opts: CaptchaProviderOpts = {},
) {
  const lang = useBackendLang();
  return useQuery({
    queryKey: [
      "product",
      "image-search",
      input.channel,
      input.imageId,
      input.pageNo,
      input.pageSize,
      lang,
    ],
    queryFn: () =>
      withCaptchaRetry(
        (captchaToken) =>
          searchProductsByImage(
            { baseUrl: API_BASE_URL },
            input,
            captchaToken === undefined ? { lang } : { captchaToken, lang },
          ),
        opts.captchaTokenProvider,
      ),
    staleTime: 60 * 1000,
    enabled: !!input.imageId,
  });
}

/**
 * Infinite-scroll variant matching `useProductSearch` shape — catalog page
 * uses the same render path (sentinel + fetchNextPage), so swapping in this
 * hook when `imageId` is present keeps the UI consistent.
 */
export function useProductImageSearchInfinite(
  input: Omit<ProductImageSearchInput, "pageNo">,
  opts: CaptchaProviderOpts = {},
) {
  const lang = useBackendLang();
  return useInfiniteQuery({
    queryKey: [
      "product",
      "image-search-infinite",
      input.channel,
      input.imageId,
      input.pageSize,
      lang,
    ],
    queryFn: ({ pageParam = 1 }) =>
      withCaptchaRetry(
        (captchaToken) =>
          searchProductsByImage(
            { baseUrl: API_BASE_URL },
            { ...input, pageNo: pageParam as number },
            captchaToken === undefined ? { lang } : { captchaToken, lang },
          ),
        opts.captchaTokenProvider,
      ),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      if (lastPage.current < lastPage.pages) return lastPage.current + 1;
      return undefined;
    },
    staleTime: 60 * 1000,
    enabled: !!input.imageId,
  });
}
